import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import LoadingSpinner from '@chownow/cocina-react-loadingspinner';

import getMissingWizardDetails from '../Restaurant/RestaurantHome/helpers';
import { setDirectPagesNotRequired } from './helpers';
import { useGetProgress } from 'api/onboarding/progressApis';

import WelcomeWizard from './WelcomeWizard';
import ShareMenu from './ShareMenu';
import SalesTax from './SalesTax';
import RestaurantHours from './RestaurantHours';
import PaymentProcessing from './PaymentProcessing';
import WebsiteAccess from './WebsiteAccess';
import WizardNavigationContextProvider from './WizardNavigationContext/WizardNavigationContextProvider';
import Promotion from './Promotion';
import LogoAndFoodImage from './LogoAndFoodImage';
import MissingSteps from './MissingSteps';
import CompletedContent from './CompletedContent';

import { ONB_WIZARD_PAGES } from 'helpers/constants';

import ErrorContent from '../Restaurant/RestaurantHome/Content/ErrorContent';

const OnboardingWizard = ({ restaurant }) => {
  const navigate = useNavigate();
  const restaurantUuid = restaurant?.sfdc_restaurant_uuid;
  const isOnboarding = restaurant?.is_onboarding;

  const {
    hasGetProgressRequestError,
    isGetProgressRequestLoading,
    getProgressRequestResponse,
    getProgress
  } = useGetProgress(restaurantUuid, isOnboarding);

  useEffect(() => {
    if (restaurantUuid) {
      void getProgress(restaurantUuid);
    }
  }, [window.location.href, restaurantUuid]);

  useEffect(() => {
    // If restaurant does not have a UUID, redirect them to the orders page
    // TODO: this should be FF'd to either legacy or portal homepage
    if (restaurantUuid === null) {
      navigate(`/restaurant/${restaurant?.id}/orders`);
    }
  }, []);

  if (isGetProgressRequestLoading) {
    return <LoadingSpinner size="x-large" isCentered />;
  }

  if (hasGetProgressRequestError) {
    return <ErrorContent />;
  }

  const missingWizardDetails = getMissingWizardDetails(
    setDirectPagesNotRequired(getProgressRequestResponse)
  );

  const getRedirectPath = () => {
    // If there are missing steps, go to missing steps page
    // Otherwise, go to completed page
    return missingWizardDetails.length === 0
      ? ONB_WIZARD_PAGES.completed
      : ONB_WIZARD_PAGES.missingSteps;
  };

  return (
    <WizardNavigationContextProvider uuid={restaurantUuid}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={getRedirectPath()} replace />}
        />
        <Route
          path="/welcome"
          element={<WelcomeWizard restaurant={restaurant} />}
        />
        <Route
          path="/share-menu"
          element={<ShareMenu restaurant={restaurant} />}
        />
        <Route
          path="/sales-tax"
          element={<SalesTax restaurant={restaurant} />}
        />
        <Route
          path="/restaurant-hours"
          element={<RestaurantHours uuid={restaurantUuid} />}
        />
        <Route
          path="/website-access"
          element={<WebsiteAccess restaurant={restaurant} />}
        />
        <Route
          path="/logo-and-food-image"
          element={<LogoAndFoodImage uuid={restaurantUuid} />}
        />
        <Route
          path="/promotion"
          element={<Promotion restaurantUuid={restaurantUuid} />}
        />
        <Route
          path="/payment-processing"
          element={<PaymentProcessing restaurant={restaurant} />}
        />
        <Route
          path="/missing-steps"
          element={<MissingSteps
            restaurant={restaurant}
            missingWizardDetails={missingWizardDetails}
          />}
        />
        <Route
          path="/completed"
          element={<CompletedContent restaurant={restaurant} />}
        />
      </Routes>
    </WizardNavigationContextProvider>
  );
};

export default OnboardingWizard;
